/**
 * TIER SERVICE - Helper functions for tier management
 * Location: src/lib/tierService.ts
 * 
 * This service provides tier-related queries and mutations
 * Abstracts tier logic from components
 */

import { supabase } from './supabase';
import type {
  TierWithFeatures,
  UserTierInfo,
  UserSubscription,
  UserMonthlyQuota,
} from '../types/tiers';

/**
 * Get all active tiers (features now in columns)
 */
export async function getAllTiers(): Promise<TierWithFeatures[]> {
  const { data: tiers, error } = await supabase
    .from('tiers')
    .select('*')
    .eq('is_active', true);

  if (error) throw error;

  // Convert columns to features object for backward compatibility
  return (tiers || []).map(tier => ({
    ...tier,
    features: {
      monthly_generations: tier.monthly_generations,
      marketplace_commission: String(tier.marketplace_commission),
      featured_listing_price: String(tier.featured_listing_price),
      support_level: tier.support_level,
      white_label_api: String(tier.white_label_api),
      refills_on_billing_cycle: String(tier.refills_on_billing_cycle),
      max_listings: String(tier.max_listings),
      custom_branding: String(tier.custom_branding),
      priority_generation: String(tier.priority_generation),
    }
  }));
}

/**
 * Get single tier by name (features now in columns)
 */
export async function getTierByName(tierName: string): Promise<TierWithFeatures | null> {
  const { data: tier, error } = await supabase
    .from('tiers')
    .select('*')
    .eq('name', tierName)
    .eq('is_active', true)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  if (!tier) return null;

  // Convert columns to features object for backward compatibility
  return {
    ...tier,
    features: {
      monthly_generations: tier.monthly_generations,
      marketplace_commission: String(tier.marketplace_commission),
      featured_listing_price: String(tier.featured_listing_price),
      support_level: tier.support_level,
      white_label_api: String(tier.white_label_api),
      refills_on_billing_cycle: String(tier.refills_on_billing_cycle),
      max_listings: String(tier.max_listings),
      custom_branding: String(tier.custom_branding),
      priority_generation: String(tier.priority_generation),
    }
  };
}

/**
 * Get user's current tier and subscription info
 */
export async function getUserTierInfo(userId: string): Promise<UserTierInfo | null> {
  // Get current subscription
  const { data: subscription, error: subError } = await supabase
    .from('user_subscriptions')
    .select('*')
    .eq('user_id', userId)
    .in('status', ['active', 'past_due'])
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (subError) {
    throw subError;
  }

  // If no subscription, assign free tier
  let tier = await getTierByName('free');

  if (!tier) {
    // Fallback if tier fetch fails - should never happen
    return null;
  }

  if (subscription) {
    const subscribedTier = await getTierByName(subscription.tier_id);
    if (subscribedTier) {
      tier = subscribedTier;
    } else {
      // Fallback: query tier by ID directly
      const { data: tierData, error: tierError } = await supabase
        .from('tiers')
        .select('*')
        .eq('id', subscription.tier_id)
        .single();

      if (tierError) throw tierError;

      tier = {
        ...tierData,
        features: {
          monthly_generations: tierData.monthly_generations,
          marketplace_commission: String(tierData.marketplace_commission),
          featured_listing_price: String(tierData.featured_listing_price),
          support_level: tierData.support_level,
          white_label_api: String(tierData.white_label_api),
        }
      } as TierWithFeatures;
    }
  }

  // Get token balance from wallet instead of monthly quotas
  const { data: wallet } = await supabase
    .from('user_wallet')
    .select('token_balance, lifetime_tokens_earned, lifetime_tokens_spent')
    .eq('user_id', userId)
    .maybeSingle();

  // If no wallet exists, create one with signup bonus
  let tokenBalance = 0;
  if (!wallet) {
    // Wallet doesn't exist - grant signup tokens
    const { data: grantResult } = await supabase.rpc('grant_tokens', {
      p_user_id: userId,
      p_amount: 10,
      p_source: 'signup_bonus'
    });
    tokenBalance = grantResult?.new_balance || 10;
  } else {
    tokenBalance = wallet.token_balance;
  }

  // Build quota info using wallet balance
  const monthlyGenerationLimit = tier?.features.monthly_generations === 'unlimited' 
    ? 999999 
    : parseInt(tier?.features.monthly_generations || '10');

  const userQuota: UserMonthlyQuota = {
    quota_limit: monthlyGenerationLimit,
    generations_used: wallet?.lifetime_tokens_spent || 0,
    refilled_packs: 0, // Not used in wallet system
    remaining_generations: tokenBalance,
    current_month: new Date().toISOString().slice(0, 7), // YYYY-MM
  };

  return {
    tier: tier!,
    subscription: subscription || ({} as UserSubscription),
    quota: userQuota,
    is_free: tier?.name === 'free',
    is_creator: tier?.name === 'creator',
    is_studio: tier?.name === 'studio',
  };
}

/**
 * Get marketplace commission for user's tier
 */
export async function getUserCommissionRate(userId: string): Promise<number> {
  const tierInfo = await getUserTierInfo(userId);
  if (!tierInfo) return 30; // Default free tier

  const commissionStr = tierInfo.tier.features.marketplace_commission || '30';
  return parseInt(commissionStr);
}

/**
 * Get featured listing price for user's tier
 */
export async function getUserFeaturedPrice(userId: string): Promise<number> {
  const tierInfo = await getUserTierInfo(userId);
  if (!tierInfo) return 5; // Default free tier = $5/week

  const priceStr = tierInfo.tier.features.featured_listing_price || '5';
  return parseInt(priceStr);
}

/**
 * @deprecated Token bundles now managed in Stripe, not database
 */
export async function getUserRefillPack(_userId: string): Promise<null> {
  return null;
}
/**
 * Record a generation usage by spending tokens
 */
export async function recordGenerationUsage(userId: string, count: number = 1, referenceId?: string): Promise<void> {
  const { data, error } = await supabase.rpc('spend_tokens', {
    p_user_id: userId,
    p_amount: count,
    p_reference_id: referenceId || null,
    p_metadata: { type: 'generation' }
  });

  if (error) throw error;
  if (data && !data.success) {
    throw new Error(data.error || 'Failed to spend tokens');
  }
}

/**
 * Check if user has sufficient generations remaining
 */
export async function checkGenerationQuota(userId: string, required: number = 1): Promise<boolean> {
  const tierInfo = await getUserTierInfo(userId);
  if (!tierInfo) return false;

  return tierInfo.quota.remaining_generations >= required;
}

/**
 * @deprecated Tier migration history removed - use subscription_history instead
 */
export async function recordTierMigration(
  _userId: string,
  _toTierName: string,
  _reason: 'upgrade' | 'downgrade' | 'free_to_paid' | 'canceled'
): Promise<void> {
  // Deprecated — tier changes tracked in subscription_history
}

/**
 * @deprecated Tier migration history removed - use subscription_history instead
 */
export async function getTierMigrationHistory(
  _userId: string,
  _limit: number = 10
): Promise<[]> {
  // Deprecated — use subscription_history instead
  return [];
}

/**
 * @deprecated Refill packs removed - use Stripe token bundles instead
 */
export async function getAvailableRefillPacks(_userId: string): Promise<[]> {
  // Deprecated — token bundles managed in Stripe
  return [];
}

/**
 * @deprecated Refill purchases removed - token bundles handled by Stripe webhook
 */
export async function recordRefillPurchase(
  _userId: string,
  _refillPackId: string,
  _quantity: number = 1,
  _priceCents: number,
  _stripePaymentIntentId?: string
): Promise<null> {
  // Deprecated — token purchases handled by Stripe webhook
  return null;
}

export default {
  getAllTiers,
  getTierByName,
  getUserTierInfo,
  getUserCommissionRate,
  getUserFeaturedPrice,
  recordGenerationUsage,
  checkGenerationQuota,
};
